import { inject, Injectable } from '@angular/core';
import { AppMasterDBFacade, InputAppClientDBFacade, ShiftsFacade } from '@monorepo/core-data';
import { AppNotificationUtilClass, InputAppDataService } from '@monorepo/services';
import {
  TProductionReasonDTO,
  TSelectedProductionReason,
  TREQUEST_GET_SESSION_BLOCKS_WITH_REASONS,
  TREQUEST_SAVE_SESSION_BLOCK_REASONS,
  TBctxItemWithoutSize,
} from '@monorepo/types';
import { DateUtils } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, from, map, Observable, of, switchMap, take } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { ProductionHourToReasonMapComponent } from '../components/production-hour-to-reason-map/production-hour-to-reason-map.component';

@Injectable({
  providedIn: 'root',
})
export class ProductionReasonLoggingService {
  private appMasterDBFacade = inject(AppMasterDBFacade);
  private inputAppClientDBFacade = inject(InputAppClientDBFacade);
  private inputAppDataService = inject(InputAppDataService);
  private appNotificationUtilClass = inject(AppNotificationUtilClass);
  private translateService = inject(TranslateService);
  private modalController = inject(ModalController);
  private shiftsFacade = inject(ShiftsFacade);

  async openProductionHourToReasonMap(isPerPieceApp: boolean) {
    const modal = await this.modalController.create({
      component: ProductionHourToReasonMapComponent,
      backdropDismiss: false,
      cssClass: 'roaming-qc-select-operation-modal',
      componentProps: {
        isPerPieceApp,
      },
    });
    modal.present();
  }

  save(bctx: TBctxItemWithoutSize[], reasons: string[], session_block: string, versioned_id?: string | null) {
    return this.withLoader('Saving production reason', () => {
      const sessionDate = DateUtils.getCrntDate();

      return this.inputAppClientDBFacade.getSelectedStationAndShiftDTO().pipe(
        take(1),
        switchMap((stationDetails) => {
          return this.appMasterDBFacade.getClientInfo().pipe(
            take(1),
            map(({ subject_key }) => [stationDetails, subject_key] as const),
          );
        }),
        switchMap(([stationDetails, subject_key]) => {
          return this.shiftsFacade.getSelectedShift().pipe(
            take(1),
            map((shift) => [stationDetails, subject_key, shift] as const),
          );
        }),
        switchMap(([stationDetails, subject_key, shift]) => {
          return this.appMasterDBFacade.getAppDetails().pipe(
            take(1),
            map((appDetails) => [stationDetails, subject_key, shift, appDetails] as const),
          );
        }),
        switchMap(([stationDetails, subject_key, shift, appDetails]) => {
          const payload: TREQUEST_SAVE_SESSION_BLOCK_REASONS = {
            subject_key,
            reasons,
            subject: stationDetails?.subject?.subject || '',
            date: sessionDate,
            bctx: bctx,
            session_block: session_block,
            shift_id: shift?.id || '',
            username: appDetails.urNm,
          };

          if (versioned_id) {
            payload.versioned_id = versioned_id;
          }
          return this.inputAppDataService.saveSessionBlockReasons(payload).pipe(
            map((res) => {
              if (res.status === 'ERROR') {
                this.appNotificationUtilClass.showPopup('Error', res.message || 'Failed to save production reason');
              }
              return res;
            }),
          );
        }),
        catchError(() => {
          this.appNotificationUtilClass.showPopup('Error', 'Failed to save production reason');
          return of(null);
        }),
      );
    });
  }

  loadSessionPlans(bctx: TBctxItemWithoutSize[]) {
    return this.withLoader('Loading session plans', () =>
      this.appMasterDBFacade.getSelectedStation().pipe(
        take(1),
        switchMap((station) =>
          this.appMasterDBFacade.getClientInfo().pipe(
            take(1),
            map(({ subject_key }) => [station?.subject || '', subject_key] as const),
          ),
        ),

        switchMap(([station, subject_key]) => {
          return this.shiftsFacade.getSelectedShift().pipe(
            take(1),
            map((shift) => [station, subject_key, shift] as const),
          );
        }),
        switchMap(([subject, subject_key, shift]) => {
          const payload: TREQUEST_GET_SESSION_BLOCKS_WITH_REASONS = {
            subject,
            subject_key,
            session_date: DateUtils.getCrntDate(),
            bctx,
            shift_id: shift?.id || '',
          };

          return this.inputAppDataService.getSessionBlocksWithReasons(payload).pipe(
            map((res) => {
              if (res.status === 'SUCCESS') {
                return res.data;
              }
              throw new Error(res.message || 'Failed to load session plans');
            }),
          );
        }),
      ),
    );
  }

  private withLoader<T>(loadingMessageKey: string, operation: () => Observable<T>): Observable<T> {
    let loaderRef: HTMLIonLoadingElement | null = null;
    return from(this.appNotificationUtilClass.presentLoading(this.translateService.instant(loadingMessageKey))).pipe(
      switchMap((loader) => {
        loaderRef = loader;
        return operation();
      }),
      finalize(() => {
        if (loaderRef) {
          this.appNotificationUtilClass.dismissLoadingV2(loaderRef);
        }
      }),
    );
  }

  getProductionReasonList() {
    return this.withLoader('Loading production reason list', () =>
      this.appMasterDBFacade.getSubjectKey().pipe(
        take(1),
        switchMap((subjectKey) => {
          return this.appMasterDBFacade.getConfigurations().pipe(
            map(({ productionReasonDepartmentId }) => [productionReasonDepartmentId, subjectKey]),
            take(1),
          );
        }),
        switchMap(([departmentId, subjectKey]) => {
          return this.inputAppDataService.getProductionReasonList({ factoryId: subjectKey, department: departmentId });
        }),
      ),
    );
  }

  transformProductionReasonList(
    productionReasonList: TProductionReasonDTO[],
    selectedReasons: string[],
  ): TSelectedProductionReason[] {
    return productionReasonList.map((reason) => {
      return {
        reason,
        selected: selectedReasons.includes(reason.reasonName) ? true : false,
      };
    });
  }
}
